/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BillingAccountACHTokenCreateDTO } from '../models/BillingAccountACHTokenCreateDTO';
import type { BillingAccountCCCreateDTO } from '../models/BillingAccountCCCreateDTO';
import type { BillingAccountCCTokenPayFrameAttribCreateDTO } from '../models/BillingAccountCCTokenPayFrameAttribCreateDTO';
import type { BillingAccountCCTokenUrlFormCreateDTO } from '../models/BillingAccountCCTokenUrlFormCreateDTO';
import type { BulkTokenCreate } from '../models/BulkTokenCreate';
import type { CCPaymentCreatePayFrameAttribsResponseDTO } from '../models/CCPaymentCreatePayFrameAttribsResponseDTO';
import type { CCTokenUrlFormDetailsDTO } from '../models/CCTokenUrlFormDetailsDTO';
import type { ChargeToken } from '../models/ChargeToken';
import type { TokenDetails } from '../models/TokenDetails';
import type { TokenUpdate } from '../models/TokenUpdate';
import type { TransactionDetails } from '../models/TransactionDetails';
import { request as __request } from '../core/request';

export class TokensVService {

    /**
     * @param billingAccountId
     * @returns TokenDetails OK
     * @throws ApiError
     */
    public static async tokensVGetAll(
        billingAccountId: string,
    ): Promise<Array<TokenDetails>> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns TokenDetails Created
     * @throws ApiError
     */
    public static async tokensVCreateAch(
        billingAccountId: string,
        requestBody?: BillingAccountACHTokenCreateDTO,
    ): Promise<TokenDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param tokenId
     * @returns TokenDetails OK
     * @throws ApiError
     */
    public static async tokensVGetById(
        billingAccountId: string,
        tokenId: string,
    ): Promise<TokenDetails> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/${tokenId}`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param tokenId
     * @param requestBody
     * @returns TokenDetails OK
     * @throws ApiError
     */
    public static async tokensVUpdate(
        billingAccountId: string,
        tokenId: string,
        requestBody?: TokenUpdate,
    ): Promise<TokenDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/${tokenId}`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns CCTokenUrlFormDetailsDTO OK
     * @throws ApiError
     */
    public static async tokensVGenerateUrlForm(
        billingAccountId: string,
        requestBody?: BillingAccountCCTokenUrlFormCreateDTO,
    ): Promise<CCTokenUrlFormDetailsDTO> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/create_cc_form`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns CCPaymentCreatePayFrameAttribsResponseDTO OK
     * @throws ApiError
     */
    public static async tokensVCreatePayFrameAttribs(
        billingAccountId: string,
        requestBody?: BillingAccountCCTokenPayFrameAttribCreateDTO,
    ): Promise<CCPaymentCreatePayFrameAttribsResponseDTO> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/create_payframe_attribs`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param tokenId
     * @param isRecurring
     * @returns TokenDetails OK
     * @throws ApiError
     */
    public static async tokensVUpdateRecurring(
        billingAccountId: string,
        tokenId: string,
        isRecurring?: boolean,
    ): Promise<TokenDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/${tokenId}/recurring`,
            query: {
                'isRecurring': isRecurring,
            },
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param tokenId
     * @param isRecurring
     * @returns TokenDetails OK
     * @throws ApiError
     */
    public static async tokensVUpdateRecurringTemporary(
        billingAccountId: string,
        tokenId: string,
        isRecurring?: boolean,
    ): Promise<TokenDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/${tokenId}/recurring/temporary`,
            query: {
                'isRecurring': isRecurring,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param tokenId
     * @param isPrimary
     * @returns TokenDetails OK
     * @throws ApiError
     */
    public static async tokensVUpdatePrimary(
        billingAccountId: string,
        tokenId: string,
        isPrimary?: boolean,
    ): Promise<TokenDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/${tokenId}/primary`,
            query: {
                'isPrimary': isPrimary,
            },
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param tokenId
     * @param requestBody
     * @returns TransactionDetails OK
     * @throws ApiError
     */
    public static async tokensVCharge(
        billingAccountId: string,
        tokenId: string,
        requestBody?: ChargeToken,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/${tokenId}/charge`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns TokenDetails OK
     * @throws ApiError
     */
    public static async tokensVCreateCc(
        billingAccountId: string,
        requestBody?: BillingAccountCCCreateDTO,
    ): Promise<TokenDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/create_cc`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param pendingCcTokenId
     * @param isSaved
     * @returns TokenDetails OK
     * @throws ApiError
     */
    public static async tokensVGetStatusById(
        billingAccountId: string,
        pendingCcTokenId: string,
        isSaved?: boolean,
    ): Promise<TokenDetails> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/pending/${pendingCcTokenId}`,
            query: {
                'isSaved': isSaved,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns TokenDetails Created
     * @throws ApiError
     */
    public static async tokensVCreateBulk(
        billingAccountId: string,
        requestBody?: Array<BulkTokenCreate>,
    ): Promise<Array<TokenDetails>> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/tokens/bulk`,
            body: requestBody,
        });
        return result.body;
    }

}